// Product rail (CONTRACT "Product rail", D6 two-mains): 56 px, tiles 56 x 44
// with an 18 px glyph and a 12 px text label; the two mains, a spacer, System
// pinned. Selected: raised fill, accent glyph, 600 label. Unread: 3 px accent
// pill on the left edge, never a number.
import type { ComponentType } from 'react';
import {
  ChatsCircle,
  Flask,
  GearSix,
  MagnifyingGlass,
  type IconProps,
} from '@phosphor-icons/react';
import { useView, type ViewId } from '../router/useView';
import type { WorkspaceRole } from '../api/types';
import { cx } from '../ui/cx';
import { groupOf, railGroupsForRole, type RailGroupId } from './destinations';

const ICONS: Readonly<Record<RailGroupId, ComponentType<IconProps>>> = {
  calls: ChatsCircle,
  desk: Flask,
  analysis: MagnifyingGlass,
  system: GearSix,
};

export interface ProductRailProps {
  // Groups with unread activity (a 3 px pill). Alerts unread comes from the summary.
  readonly unread?: ReadonlySet<RailGroupId>;
  readonly onNavigate?: (view: ViewId) => void;
  readonly role?: WorkspaceRole | null;
}

export function ProductRail({ unread, onNavigate, role = null }: ProductRailProps) {
  const { view, navigate } = useView();
  const active = groupOf(view).id;
  const go = (id: ViewId) => {
    navigate(id);
    onNavigate?.(id);
  };
  const groups = railGroupsForRole(role);
  const tiles = groups.map((group) => {
    const Icon = ICONS[group.id];
    const selected = group.id === active;
    return (
      <a
        key={group.id}
        href={`?view=${group.home}`}
        className={cx('rail-tile', selected && 'rail-tile-on')}
        aria-current={selected ? 'page' : undefined}
        aria-label={`${group.label}: ${group.destinations.map((d) => d.label).join(', ')}`}
        onClick={(event) => {
          event.preventDefault();
          go(group.home);
        }}
      >
        {unread?.has(group.id) ? <span className="rail-unread" aria-hidden="true" /> : null}
        <Icon className="rail-ic" aria-hidden="true" />
        <span className="rail-lbl">{group.label}</span>
      </a>
    );
  });
  const firstPinned = groups.findIndex((group) => group.pinned);
  return (
    <nav className="rail" aria-label="Product rail" data-region="rail">
      {tiles.slice(0, firstPinned)}
      <span className="rail-spacer" />
      {tiles.slice(firstPinned)}
    </nav>
  );
}
